import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

import type { ProductType } from '@xing-com/crate-common-graphql-types';

type PerkListDiscovery = {
  /** Selects the discovered product in the perk list and scrolls it into view. */
  discoverPerks: (product: ProductType) => void;
  /**
   * Changes on every discovery so the perk list is re-initialised with the
   * discovered product, even when it is discovered twice in a row.
   */
  perkListKey: number;
  perkListProduct: ProductType;
  perkListRef: RefObject<HTMLElement | null>;
};

export const usePerkListDiscovery = (
  product: ProductType
): PerkListDiscovery => {
  const perkListRef = useRef<HTMLElement>(null);
  const [perkListProduct, setPerkListProduct] = useState(product);
  const [discoveries, setDiscoveries] = useState(0);

  useEffect(() => {
    setPerkListProduct(product);
  }, [product]);

  const discoverPerks = useCallback((discoveredProduct: ProductType) => {
    setPerkListProduct(discoveredProduct);
    setDiscoveries((previousDiscoveries) => previousDiscoveries + 1);
    perkListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  return {
    discoverPerks,
    perkListKey: discoveries,
    perkListProduct,
    perkListRef,
  };
};
